import { FunctionComponent, ReactNode, useMemo } from 'react';
import ArrowDownIcon from '@heroicons/react/24/solid/ArrowDownIcon';
import ArrowUpIcon from '@heroicons/react/24/solid/ArrowUpIcon';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    CardProps,
    LinearProgress,
    Skeleton,
    Stack,
    SvgIcon,
    Typography,
} from '@mui/material';

type DataBadgeType = 'difference' | 'progress' | 'plain';

type BaseData = {
    value: string;
};

type DifferenceData = BaseData & {
    difference: number;
    positive: boolean;
};

type ProgressData = {
    value: number;
};

type DataBadgeProps<T extends DataBadgeType> = {
    sx?: CardProps['sx'];
    title: string;
    type: T;
    data: T extends 'difference'
        ? DifferenceData
        : T extends 'progress'
          ? ProgressData
          : BaseData;
    avatarIcon: ReactNode;
    avatarColor: string;
    loading?: boolean;
};

// only use function for generics
export function DataBadge<T extends DataBadgeType>({
    sx,
    title,
    type,
    data,
    avatarIcon,
    avatarColor,
    loading = true,
}: DataBadgeProps<T>) {
    const { value } = data;

    const dataSection = useMemo(() => {
        switch (type) {
            case 'difference':
                return <DifferenceSection {...(data as DifferenceData)} />;
            case 'progress':
                return <ProgressSection {...(data as ProgressData)} />;
            default:
                return null;
        }
    }, [type]);

    return (
        <Card
            sx={{
                ...sx,
                minHeight: 170,
            }}
        >
            <CardContent>
                <Stack
                    alignItems='flex-start'
                    direction='row'
                    justifyContent='space-between'
                    spacing={3}
                >
                    <Stack spacing={1}>
                        <Typography color='text.secondary' variant='overline'>
                            {title}
                        </Typography>
                        <Typography variant='h4'>
                            {loading ? (
                                <Skeleton width={100} animation='wave' />
                            ) : type === 'progress' ? (
                                `${value}%`
                            ) : (
                                value
                            )}
                        </Typography>
                    </Stack>
                    <Avatar
                        sx={{
                            backgroundColor: avatarColor,
                            height: 56,
                            width: 56,
                        }}
                    >
                        <SvgIcon>{avatarIcon}</SvgIcon>
                    </Avatar>
                </Stack>

                {loading ? (
                    <Skeleton height={50} animation='wave' />
                ) : (
                    dataSection
                )}
            </CardContent>
        </Card>
    );
}

const DifferenceSection: FunctionComponent<DifferenceData> = ({
    difference,
    positive,
}) => {
    return (
        <Stack alignItems='center' direction='row' spacing={2} sx={{ mt: 2 }}>
            <Stack alignItems='center' direction='row' spacing={0.5}>
                <SvgIcon
                    color={positive ? 'success' : 'error'}
                    fontSize='small'
                >
                    {positive ? <ArrowUpIcon /> : <ArrowDownIcon />}
                </SvgIcon>
                <Typography
                    color={positive ? 'success.main' : 'error.main'}
                    variant='body2'
                >
                    {difference}%
                </Typography>
            </Stack>
            <Typography color='text.secondary' variant='caption'>
                Seit letztem Monat
            </Typography>
        </Stack>
    );
};

const ProgressSection: FunctionComponent<ProgressData> = ({ value }) => (
    <Box sx={{ mt: 3 }}>
        <LinearProgress value={value} variant='determinate' />
    </Box>
);
